/**
 * WebAuthn/Passkey hook for managing passkey registration and authentication
 */

import { useState, useCallback, useEffect } from "react"
import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser"
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/types"
import { supabase } from "@/integrations/supabase/client"
import { isPlatformAuthenticatorAvailable } from "@/utils/webauthn"

export interface WebAuthnCredential {
  id: string
  name: string
  device_type: string
  backed_up: boolean
  last_used_at: string | null
  created_at: string
}

interface UseWebAuthnReturn {
  // State
  isSupported: boolean
  isLoading: boolean
  error: string | null
  credentials: WebAuthnCredential[]

  // Registration
  registerPasskey: (credentialName?: string) => Promise<boolean>

  // Authentication
  authenticateWithPasskey: (email?: string) => Promise<{
    success: boolean
    tokenHash?: string
    email?: string
    error?: string
  }>

  // Credential management
  loadCredentials: () => Promise<void>
  deleteCredential: (credentialId: string) => Promise<boolean>
  renameCredential: (credentialId: string, newName: string) => Promise<boolean>

  // Clear error
  clearError: () => void
}

export function useWebAuthn(): UseWebAuthnReturn {
  const [isSupported, setIsSupported] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([])

  // Check platform support on mount
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await isPlatformAuthenticatorAvailable()
      setIsSupported(supported)
    }
    checkSupport()
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Load user's credentials
  const loadCredentials = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from("webauthn_credentials" as "audit_logs")
        .select("id, name, device_type, backed_up, last_used_at, created_at")
        .order("created_at", { ascending: false })

      if (fetchError) {
        console.error("Error loading credentials:", fetchError)
        return
      }

      setCredentials((data as unknown as WebAuthnCredential[]) || [])
    } catch (err) {
      console.error("Exception loading credentials:", err)
    }
  }, [])

  // Register a new passkey
  const registerPasskey = useCallback(async (credentialName?: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Get registration options from Edge Function
      const { data: optionsData, error: optionsError } = await supabase.functions.invoke(
        "webauthn-register-options"
      )

      if (optionsError) {
        setError(optionsError.message || "Failed to get registration options")
        return false
      }

      const { options, webauthnUserId } = optionsData as {
        options: PublicKeyCredentialCreationOptionsJSON
        webauthnUserId: string
      }

      // Step 2: Start registration with browser
      let registrationResponse: RegistrationResponseJSON
      try {
        registrationResponse = await startRegistration({ optionsJSON: options })
      } catch (browserError) {
        if (browserError instanceof Error) {
          if (browserError.name === "NotAllowedError") {
            setError("Registration was cancelled or timed out")
          } else if (browserError.name === "InvalidStateError") {
            setError("This passkey is already registered")
          } else {
            setError(browserError.message)
          }
        } else {
          setError("Failed to create passkey")
        }
        return false
      }

      // Step 3: Verify registration with Edge Function
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
        "webauthn-register-verify",
        {
          body: {
            response: registrationResponse,
            webauthnUserId,
            credentialName: credentialName || "Passkey",
          },
        }
      )

      if (verifyError) {
        setError(verifyError.message || "Failed to verify registration")
        return false
      }

      if (!verifyData?.verified) {
        setError("Registration verification failed")
        return false
      }

      // Reload credentials
      await loadCredentials()

      return true
    } catch (err) {
      console.error("Registration error:", err)
      setError(err instanceof Error ? err.message : "An unexpected error occurred")
      return false
    } finally {
      setIsLoading(false)
    }
  }, [loadCredentials])

  // Authenticate with passkey
  const authenticateWithPasskey = useCallback(async (email?: string): Promise<{
    success: boolean
    tokenHash?: string
    email?: string
    error?: string
  }> => {
    setIsLoading(true)
    setError(null)

    try {
      // Step 1: Get authentication options from Edge Function
      const { data: optionsData, error: optionsError } = await supabase.functions.invoke(
        "webauthn-auth-options",
        {
          body: email ? { email } : {},
        }
      )

      if (optionsError) {
        // Check for specific error about no passkeys
        const errorMessage = optionsError.message || ""
        let errorToSet: string
        if (errorMessage.includes("No passkeys registered")) {
          errorToSet = "No passkeys registered for this account"
        } else {
          errorToSet = errorMessage || "Failed to get authentication options"
        }
        setError(errorToSet)
        return { success: false, error: errorToSet }
      }

      const { options } = optionsData as {
        options: PublicKeyCredentialRequestOptionsJSON
      }

      // Step 2: Start authentication with browser
      let authenticationResponse: AuthenticationResponseJSON
      try {
        authenticationResponse = await startAuthentication({ optionsJSON: options })
      } catch (browserError) {
        let errorToSet: string
        if (browserError instanceof Error) {
          if (browserError.name === "NotAllowedError") {
            errorToSet = "Authentication was cancelled or timed out"
          } else {
            errorToSet = browserError.message
          }
        } else {
          errorToSet = "Failed to authenticate with passkey"
        }
        setError(errorToSet)
        return { success: false, error: errorToSet }
      }

      // Step 3: Verify authentication with Edge Function
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
        "webauthn-auth-verify",
        {
          body: {
            response: authenticationResponse,
            challenge: options.challenge,
          },
        }
      )

      if (verifyError) {
        const errorToSet = verifyError.message || "Failed to verify authentication"
        setError(errorToSet)
        return { success: false, error: errorToSet }
      }

      if (!verifyData?.verified) {
        const errorToSet = "Authentication verification failed"
        setError(errorToSet)
        return { success: false, error: errorToSet }
      }

      return {
        success: true,
        tokenHash: verifyData.tokenHash,
        email: verifyData.email,
      }
    } catch (err) {
      console.error("Authentication error:", err)
      const errorToSet = err instanceof Error ? err.message : "An unexpected error occurred"
      setError(errorToSet)
      return { success: false, error: errorToSet }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Delete a credential
  const deleteCredential = useCallback(async (credentialId: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from("webauthn_credentials" as "audit_logs")
        .delete()
        .eq("id" as "action", credentialId)

      if (deleteError) {
        setError(deleteError.message || "Failed to delete passkey")
        return false
      }

      // Reload credentials
      await loadCredentials()
      return true
    } catch (err) {
      console.error("Delete error:", err)
      setError(err instanceof Error ? err.message : "An unexpected error occurred")
      return false
    } finally {
      setIsLoading(false)
    }
  }, [loadCredentials])

  // Rename a credential (not currently supported via RLS, would need Edge Function)
  const renameCredential = useCallback(async (
    _credentialId: string,
    _newName: string
  ): Promise<boolean> => {
    // This would require an Edge Function since we don't allow UPDATE via RLS
    setError("Renaming passkeys is not supported yet")
    return false
  }, [])

  return {
    isSupported,
    isLoading,
    error,
    credentials,
    registerPasskey,
    authenticateWithPasskey,
    loadCredentials,
    deleteCredential,
    renameCredential,
    clearError,
  }
}
